<script setup lang="ts">
import { useField } from 'vee-validate'
import { IMaskDirective as vMask } from 'vue-imask'
import { mergeRules } from 'core/services/form'

const originalProps = defineProps<{
  name: string
  error?: string
  disabled?: boolean
  icon?: string
  prefix?: string
  suffix?: string
  rules?: any
  mask?: any
  label?: string
  initialValue?: string
  placeholder?: string
  tooltip?: any
  requiredError?: string
  small?: boolean
  disableStoryblok?: boolean
  inputmode?: 'search' | 'text' | 'none' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal'
  inputClass?: any
  autocompleteName?: string
  autocomplete?: string
}>()
const emit = defineEmits(['changed', 'focus', 'keyup'])
const ga4 = useAnalytics()

const props = originalProps.disableStoryblok ? originalProps : useStoryblokProps(originalProps)

const mergedRules = computed(() => originalProps.disabled ? undefined : mergeRules(originalProps.rules, props.requiredError))
const { value, errorMessage, validate, setErrors } = useField(props.name, mergedRules, {
  validateOnValueUpdate: false,
})

function accept(event: any) {
  value.value = event.detail?.typedValue
}
function change(event: any, force?: boolean) {
  if (!props.mask || force) value.value = event.target.value
  emit('changed', value.value, props.name)
  validate()
}
function focus() {
  ga4.trackEvent({ event_action: 'text', event_label: originalProps?.name })
  emit('focus', originalProps?.name)
}
function keyup(event: any) {
  emit('keyup', event.key, originalProps?.name)
}
watch(errorMessage, () => {
  if (errorMessage.value) ga4.errorEvent({ event_label: originalProps?.name })
})

watch(() => originalProps.error, () => {
  originalProps.error ? setErrors(originalProps.error) : setErrors([])
})
</script>

<template>
  <div>
    <BaseLabel v-if="props.label" :tooltip="props.tooltip">
      {{ props.label }}
    </BaseLabel>
    <div
      disabled
      class="border-1 rounded-xl w-full items-center flex"
      :class="{
        'bg-gray-light border-gray': disabled,
        '!border-error text-error': errorMessage,
        'bg-white text-gray border-form shadow-form hover:border-primary hover:text-primary hover:transition-colors focus-within:border-primary focus-within:text-primary': !disabled,
        'w-full md:w-42': props.small,
      }"
    >
      <slot name="start">
        <AppIcon v-if="props.icon" size="18" icon="far-envelope" class="ml-2.5" />
        <div v-if="props.prefix" class="ml-2.5 pr-2.5 text-black border-r-1 border-gray">
          {{ props.prefix }}
        </div>
      </slot>
      <input
        v-mask="mask"
        :name="autocomplete === 'off' ? undefined : autocompleteName || props.name"
        :data-test-id="`FieldText|${name}`"
        :disabled="disabled"
        :placeholder="props.placeholder"
        :value="value"
        :class="inputClass"
        class="text-black  p-2.5 rounded-xl focus:outline-none w-full h-full disabled:bg-gray-light"
        type="text"
        :inputmode="inputmode"
        :autocomplete="autocomplete"
        @change="change"
        @accept="accept"
        @smartform="change($event, true)"
        @focus="focus"
        @keyup="keyup"
      >
      <slot name="end">
        <div v-if="props.suffix" class="mr-2.5 text-black">
          {{ props.suffix }}
        </div>
      </slot>
    </div>
    <FormFieldErrorMessage v-if="errorMessage">
      {{ errorMessage }}
    </FormFieldErrorMessage>
  </div>
</template>
